import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Target, Eye, Award, Users, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/contexts/SettingsContext";

function AnimatedSection({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className={className}>
      {children}
    </motion.div>
  );
}

const stats = [
  { value: "+150", label: "مشروع منجز" },
  { value: "+50", label: "عميل سعيد" },
  { value: "+30", label: "دورة تدريبية" },
  { value: "+10", label: "سنوات خبرة" },
];

const values = [
  { icon: Target, title: "الاحترافية", desc: "نلتزم بأعلى معايير الجودة في كل مشروع نقوم به" },
  { icon: Eye, title: "الإبداع", desc: "نبتكر حلولًا فريدة تتجاوز المألوف وتحقق التميز" },
  { icon: Award, title: "التميز", desc: "نسعى دائمًا لتقديم نتائج استثنائية تفوق التوقعات" },
  { icon: Users, title: "الشراكة", desc: "نعمل كفريق واحد مع عملائنا لتحقيق أهدافهم المشتركة" },
];

export default function AboutPage() {
  const settings = useSettings();
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={settings.about_image || "https://images.pexels.com/photos/4829065/pexels-photo-4829065.jpeg?auto=compress&cs=tinysrgb&w=1920"} alt="about" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111112]/50 to-[#111112]" />
        </div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-primary/8 rounded-full blur-[150px]" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block px-4 py-1.5 rounded-full border border-primary/30 text-primary text-sm font-medium mb-4">من نحن</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Zain'] text-white leading-tight mb-4">
              نحن <span className="text-primary">رؤية</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              شركة متكاملة تقدم حلولًا احترافية في ثلاثة مجالات رئيسية: التسويق، الإنتاج، والتدريب. هدفنا بناء علامات تجارية قوية تعكس الاحترافية والجودة العالية.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#0d0d0e] border-y border-white/5">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <AnimatedSection key={i}>
                <div className="text-center" data-testid={`stat-${i}`}>
                  <div className="text-3xl md:text-4xl font-black font-['Zain'] text-primary mb-1">{s.value}</div>
                  <div className="text-zinc-400 text-sm">{s.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div className="relative">
                <img src={settings.about_team_image || "https://images.pexels.com/photos/15543037/pexels-photo-15543037.jpeg?auto=compress&cs=tinysrgb&w=800"} alt="team" className="rounded-2xl border border-white/5 w-full" />
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary/20 rounded-full blur-xl" />
              </div>
            </AnimatedSection>
            <AnimatedSection>
              <span className="text-primary text-sm font-medium">قصتنا</span>
              <h2 className="text-3xl md:text-4xl font-bold font-['Zain'] text-white mt-2 mb-6">
                رحلة نحو <span className="text-primary">التميز</span>
              </h2>
              <div className="space-y-4 text-zinc-400 leading-relaxed">
                <p>
                  انطلقنا بشغف كبير وطموح أكبر لنقدم حلولًا إبداعية متكاملة تجمع بين التسويق والإنتاج والتدريب تحت سقف واحد.
                </p>
                <p>
                  نؤمن بأن النجاح يبدأ من رؤية واضحة وتنفيذ احترافي. لذلك نحرص على فهم احتياجات عملائنا وتقديم حلول مخصصة تحقق أهدافهم.
                </p>
                <p>
                  فريقنا المتميز يجمع بين الخبرة والإبداع، مما يمكننا من تقديم خدمات عالية الجودة في مختلف المجالات.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-[#0d0d0e]">
        <div className="container mx-auto px-4 md:px-6">
          <AnimatedSection className="text-center mb-14">
            <span className="text-primary text-sm font-medium">قيمنا</span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Zain'] text-white mt-2">ما يميزنا</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <AnimatedSection key={i}>
                  <div className="bg-[#161618] border border-white/5 rounded-xl p-6 text-center hover:border-primary/20 transition-all" data-testid={`value-${i}`}>
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon size={24} className="text-primary" />
                    </div>
                    <h3 className="text-lg font-bold font-['Zain'] text-white mb-2">{v.title}</h3>
                    <p className="text-zinc-400 text-sm">{v.desc}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <AnimatedSection>
            <h2 className="text-2xl md:text-4xl font-bold font-['Zain'] text-white mb-6">
              هل تريد معرفة المزيد؟
            </h2>
            <Link to="/contact">
              <Button size="lg" className="bg-primary hover:bg-[#E54D2E] text-white px-8 red-glow" data-testid="about-cta">
                تواصل معنا
                <ArrowLeft size={16} className="ms-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
